import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, DatePicker, Input, InputNumber, Modal, Radio, Select, Spin, message } from "antd";
import { CopyOutlined, CrownOutlined, EditOutlined, PercentageOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import * as api from "../controller";
import { money } from "../../Subscription/money";
import {
  claimByText, customPlanError, customPlanPayload, describeOffer, describeOfferPlans, familyOf,
  groupFamilies, offerFormError, offerPayload, offeredToText, previewDiscount, templateGroups,
} from "./offerMath";
import { shortDate } from "./planState";
import "./billing.css";

const ok = (r) => r && r.status >= 200 && r.status < 300;

/**
 * Put a special offer on an office or a practice (all its offices), or edit one.
 *
 * Two kinds, as the backend has them (usage.PlanOffer):
 *  - Discount: $ off the monthly rate (×12 on a 12-month plan) or % off, on the
 *    public plans and billing terms ticked in the matrix.
 *  - Special plan: a private plan only this office/practice can see and buy.
 *    Special plans are kept as templates — any of them (expired offer or not) can
 *    be offered again, duplicated and edited, or edited while nobody has bought it.
 *
 * The expiry is the last day to claim; whoever buys by then keeps the price on
 * every renewal. The server re-prices and validates everything shown here.
 *
 * Editing an existing offer changes only its label and last day to claim — the
 * price terms are what buyers were promised.
 */
export default function OfferEditorModal({ open, onClose, scope, offer, onSaved }) {
  const editing = !!offer;
  const [kind, setKind] = useState("discount");
  const [discountType, setDiscountType] = useState("percent");
  const [discountValue, setDiscountValue] = useState(null);
  const [tierIds, setTierIds] = useState([]);
  const [label, setLabel] = useState("");
  const [expiresOn, setExpiresOn] = useState(null);
  const [tiers, setTiers] = useState(null);       // public plans
  const [plans, setPlans] = useState(null);       // special plans (the templates)
  const [templateKey, setTemplateKey] = useState(null);
  const [planForm, setPlanForm] = useState(null); // {mode: create|duplicate|edit, group?}
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);

  const loadPlans = useCallback(async () => {
    const r = await api.getCustomPlans();
    setPlans(ok(r) && Array.isArray(r.json) ? r.json : []);
    return ok(r) && Array.isArray(r.json) ? r.json : [];
  }, []);

  useEffect(() => {
    if (!open) return;
    setKind(offer ? offer.kind : "discount");
    setDiscountType((offer && offer.discount_type) || "percent");
    setDiscountValue(offer && offer.discount_value != null ? Number(offer.discount_value) : null);
    setTierIds(offer ? (offer.tiers || []).map((t) => t.id) : []);
    setLabel(offer ? offer.label || "" : "");
    setExpiresOn(offer ? offer.expires_on : null);
    setTemplateKey(null);
    setPlanForm(null);
    setTried(false);
    setPlans(null);
    if (!offer) {
      setTiers(null);
      api.getSubscriptionTiers().then((r) => setTiers(ok(r) && Array.isArray(r.json) ? r.json : []));
    }
  }, [open, offer]);

  useEffect(() => {
    if (open && !editing && kind === "plan" && plans === null) loadPlans();
  }, [open, editing, kind, plans, loadPlans]);

  const families = useMemo(() => groupFamilies(tiers || []), [tiers]);
  const groups = useMemo(() => templateGroups(plans || []), [plans]);
  const selectedGroup = groups.find((g) => g.key === templateKey) || null;

  const switchKind = (next) => {
    if (next === kind) return;
    setKind(next);
    setTierIds([]);
    setTemplateKey(null);
    setPlanForm(null);
    setTried(false);
  };

  const form = { kind, discountType, discountValue, tierIds, tiers: tiers || [], label, expiresOn };
  const error = editing ? null : offerFormError(form);
  const who = scope && scope.practiceId && !scope.officeId
    ? <>For every office of <strong>{scope.name}</strong></>
    : <>For <strong>{(scope && scope.name) || "this office"}</strong></>;

  const save = async () => {
    setTried(true);
    if (error || planForm) return;
    setSaving(true);
    const r = editing
      ? await api.updateOffer(offer.id, { label: label.trim(), expires_on: expiresOn || null })
      : await api.createOffer(offerPayload(form, scope));
    setSaving(false);
    if (ok(r)) {
      message.success(editing ? "Offer updated." : "Offer is on — they'll see it when they choose a plan.");
      if (onSaved) onSaved(r.json);
      onClose();
    } else {
      message.error((r.json && r.json.detail) || "Couldn't save the offer.");
    }
  };

  // A new special plan was saved: pick it, back on the template list.
  const planSaved = async (newTierIds) => {
    const list = await loadPlans();
    const g = templateGroups(list).find((x) => x.tiers.some((t) => newTierIds.includes(t.id)));
    setPlanForm(null);
    if (g) { setTemplateKey(g.key); setTierIds(g.tiers.map((t) => t.id)); }
  };

  const pickTemplate = (g) => {
    setTemplateKey(g.key);
    setTierIds(g.tiers.map((t) => t.id));
  };

  const footer = planForm ? null : (
    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
      {tried && error ? <span className="ub-editor-error">{error}</span> : null}
      <Button onClick={onClose}>Cancel</Button>
      <Button type="primary" loading={saving} onClick={save}>
        {editing ? "Save" : "Put offer on"}
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={editing ? "Edit offer" : "New special offer"}
      width={760}
      className="ub-editor"
      footer={footer}
      destroyOnClose
      // CRM drawers sit at z-index 10001 (.ant-drawer-open, Home/app.css).
      zIndex={10050}
    >
      <div className="ub-editor-for">{who}</div>

      {editing ? (
        <div className="ub-callout">
          <strong>{describeOffer(offer)}</strong>
          <div style={{ marginTop: 2 }}>
            {offer.kind === "plan"
              ? (offer.tiers || []).map((t) => t.title).join(" · ")
              : `On ${describeOfferPlans(offer)}`}
          </div>
          <div style={{ color: "#9a96a8", marginTop: 4, fontSize: 12 }}>
            The price terms can&rsquo;t change once an offer is on — revoke it and put a new one on instead.
          </div>
        </div>
      ) : (
        <div className="ub-kinds">
          <KindCard
            on={kind === "discount"}
            icon={<PercentageOutlined />}
            title="Discount"
            sub="A dollar amount or a percentage off the plans you choose."
            onClick={() => switchKind("discount")}
          />
          <KindCard
            on={kind === "plan"}
            icon={<CrownOutlined />}
            title="Special plan"
            sub="A private plan only they can see — from a saved one or a new one."
            onClick={() => switchKind("plan")}
          />
        </div>
      )}

      {!editing && kind === "discount" ? (
        tiers === null ? <Spin style={{ display: "block", margin: "24px auto" }} /> : (
          <>
            <span className="ub-step-label">Discount</span>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <Radio.Group
                optionType="button"
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value)}
                options={[
                  { value: "percent", label: "% off" },
                  { value: "amount", label: "$ off the monthly rate" },
                ]}
              />
              <InputNumber
                min={0}
                max={discountType === "percent" ? 99 : undefined}
                precision={2}
                value={discountValue}
                onChange={setDiscountValue}
                prefix={discountType === "amount" ? "$" : undefined}
                addonAfter={discountType === "percent" ? "%" : "/ month"}
                placeholder={discountType === "percent" ? "20" : "50"}
                style={{ width: 170 }}
              />
            </div>
            {discountType === "amount" ? (
              <div className="leadset-hint" style={{ marginTop: 6 }}>
                Comes off every month — so ×12 on a 12-month plan, taken off its yearly charge.
              </div>
            ) : null}

            <span className="ub-step-label">
              Plans<span className="ub-step-hint">Tick the plans and billing terms it applies to</span>
            </span>
            <PlanMatrix
              families={families}
              selected={tierIds}
              onChange={setTierIds}
              discountType={discountType}
              discountValue={discountValue}
            />
          </>
        )
      ) : null}

      {!editing && kind === "plan" ? (
        planForm ? (
          <CustomPlanForm
            key={`${planForm.mode}-${planForm.group ? planForm.group.key : "new"}`}
            init={planForm}
            scopeName={scope && scope.name}
            onCancel={() => setPlanForm(null)}
            onSaved={planSaved}
          />
        ) : plans === null ? <Spin style={{ display: "block", margin: "24px auto" }} /> : (
          <>
            <span className="ub-step-label" style={{ display: "flex", alignItems: "center" }}>
              <span>
                Special plan
                <span className="ub-step-hint">Saved plans are templates — reuse one, or make a new one</span>
              </span>
              <Button
                size="small"
                icon={<PlusOutlined />}
                style={{ marginLeft: "auto" }}
                onClick={() => setPlanForm({ mode: "create" })}
              >
                New special plan
              </Button>
            </span>
            {groups.length ? (
              <div className="ub-templates">
                {groups.map((g) => (
                  <TemplateCard
                    key={g.key}
                    group={g}
                    on={g.key === templateKey}
                    onPick={() => pickTemplate(g)}
                    onDuplicate={() => setPlanForm({ mode: "duplicate", group: g })}
                    onEdit={() => setPlanForm({ mode: "edit", group: g })}
                  />
                ))}
              </div>
            ) : (
              <div className="ub-callout">
                No special plans yet. Make one from an existing plan — it&rsquo;s saved as a template you
                can offer to other offices later.
              </div>
            )}
            {selectedGroup && selectedGroup.annual && selectedGroup.monthly ? (
              <div style={{ marginTop: 10, display: "flex", gap: 14, alignItems: "center", fontSize: 12.5 }}>
                <span style={{ color: "#3d3a4d", fontWeight: 600 }}>Offer</span>
                {[["monthly", "Monthly"], ["annual", "12-month"]].map(([cad, text]) => {
                  const id = selectedGroup[cad].id;
                  return (
                    <Checkbox
                      key={cad}
                      checked={tierIds.includes(id)}
                      onChange={(e) => setTierIds(e.target.checked
                        ? [...tierIds, id]
                        : tierIds.filter((x) => x !== id))}
                    >
                      {text}
                    </Checkbox>
                  );
                })}
              </div>
            ) : null}
          </>
        )
      ) : null}

      {!planForm ? (
        <>
          <div className="ub-form-grid" style={{ marginTop: 4 }}>
            <div>
              <span className="ub-step-label">
                Label<span className="ub-step-hint">Shown to them on the plan</span>
              </span>
              <Input
                value={label}
                maxLength={120}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={kind === "plan" ? "e.g. Partner pricing" : "e.g. Founding clinic offer"}
              />
            </div>
            <div>
              <span className="ub-step-label">
                Last day to claim<span className="ub-step-hint">Empty = no end date</span>
              </span>
              <DatePicker
                style={{ width: "100%" }}
                format="MMM D, YYYY"
                value={expiresOn ? dayjs(expiresOn) : null}
                onChange={(d) => setExpiresOn(d ? d.format("YYYY-MM-DD") : null)}
                disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
                placeholder="No end date"
                popupStyle={{ zIndex: 10060 }}
              />
            </div>
          </div>
          <div className="leadset-hint" style={{ marginTop: 8 }}>
            {claimByText(expiresOn)} (end of day, Toronto time). Whoever buys by then keeps this price on
            every renewal until they change or cancel their plan.
          </div>
        </>
      ) : null}
    </Modal>
  );
}

function KindCard({ on, icon, title, sub, onClick }) {
  return (
    <button type="button" className={`ub-kind${on ? " is-on" : ""}`} onClick={onClick}>
      <span className="ub-kind-icon">{icon}</span>
      <span>
        <div className="ub-kind-title">{title}</div>
        <div className="ub-kind-sub">{sub}</div>
      </span>
    </button>
  );
}

/** "$399/mo" or "$4,788/yr" — one charge as it is billed. */
function chargeText(tier, cents) {
  return `$${money(cents)}${tier && (tier.cadence === "annual" || Number(tier.term_months) >= 12) ? "/yr" : "/mo"}`;
}

function PriceLine({ tier, checked, discountType, discountValue }) {
  const p = previewDiscount(tier, discountType, discountValue);
  const discounted = checked && Number(discountValue) > 0;
  if (!discounted) return <div className="ub-matrix-price">{chargeText(tier, p.listCharge)}</div>;
  return (
    <div className={`ub-matrix-price ${p.belowFloor ? "is-bad" : "is-new"}`}>
      <span className="ub-strike">{chargeText(tier, p.listCharge)}</span>
      {p.belowFloor ? "below $1" : chargeText(tier, p.charge)}
    </div>
  );
}

function PlanMatrix({ families, selected, onChange, discountType, discountValue }) {
  const has = (t) => t && selected.includes(t.id);
  const setMany = (list, on) => {
    const ids = list.filter(Boolean).map((t) => t.id);
    onChange(on ? Array.from(new Set([...selected, ...ids])) : selected.filter((x) => !ids.includes(x)));
  };
  const column = (cad) => families.map((f) => f[cad]).filter(Boolean);
  const allOn = (list) => list.length > 0 && list.every(has);
  if (!families.length) return <div className="ub-callout">No plans are on sale right now.</div>;

  const cell = (t, text) => (t ? (
    <div className="ub-matrix-cell">
      <Checkbox checked={has(t)} onChange={(e) => setMany([t], e.target.checked)}>{text}</Checkbox>
      <PriceLine tier={t} checked={has(t)} discountType={discountType} discountValue={discountValue} />
    </div>
  ) : <span className="ub-matrix-na">—</span>);

  return (
    <div className="ub-matrix">
      <div className="ub-matrix-row ub-matrix-head">
        <span>Plan</span>
        {[["annual", "12-month"], ["monthly", "Monthly"]].map(([cad, text]) => (
          <span key={cad}>
            {text}{" "}
            <button type="button" className="ub-link" onClick={() => setMany(column(cad), !allOn(column(cad)))}>
              {allOn(column(cad)) ? "· none" : "· all"}
            </button>
          </span>
        ))}
        <span />
      </div>
      {families.map((f) => {
        const both = [f.annual, f.monthly].filter(Boolean);
        return (
          <div className="ub-matrix-row" key={f.family}>
            <span className="ub-matrix-family">{f.family}</span>
            {cell(f.annual, "12-month")}
            {cell(f.monthly, "Monthly")}
            <button type="button" className="ub-link" onClick={() => setMany(both, !allOn(both))}>
              {allOn(both) ? "None" : "Both"}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function TemplateCard({ group, on, onPick, onDuplicate, onEdit }) {
  const prices = [];
  if (group.monthly) prices.push(`Monthly $${money(group.monthly.price)}/mo`);
  if (group.annual) {
    prices.push(`12-month $${money(group.annual.price)}/mo ($${money(Number(group.annual.price) * 12)}/yr)`);
  }
  const base = group.basedOn ? familyOf(group.basedOn) : null;
  return (
    <div
      role="button"
      tabIndex={0}
      className={`ub-template${on ? " is-on" : ""}`}
      onClick={onPick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(); } }}
    >
      <Checkbox checked={on} style={{ marginTop: 2 }} tabIndex={-1} />
      <div className="ub-template-main">
        <div className="ub-template-name">{group.family}</div>
        <div className="ub-template-prices">{prices.join(" · ")}</div>
        <div className="ub-template-meta">
          {base ? `From ${base} · ` : ""}{offeredToText(group)}
          {group.purchases ? ` · ${group.purchases} bought` : ""}
          {` · made ${shortDate(group.createdAt)}`}
        </div>
      </div>
      <div className="ub-template-actions" onClick={(e) => e.stopPropagation()} role="presentation">
        <Button size="small" type="text" icon={<CopyOutlined />} onClick={onDuplicate}>Duplicate &amp; edit</Button>
        {group.editable ? (
          <Button size="small" type="text" icon={<EditOutlined />} onClick={onEdit}>Edit</Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Make a special plan from an existing one (or a copy of a saved one), or edit a
 * saved one nobody has bought. Limits and overage prices come from the plan it is
 * based on; the FTC caps, prices, name and feature lines are editable here.
 */
function CustomPlanForm({ init, scopeName, onCancel, onSaved }) {
  const [publicTiers, setPublicTiers] = useState(null);
  const [plan, setPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const editing = init.mode === "edit";

  useEffect(() => {
    let alive = true;
    api.getSubscriptionTiers().then((r) => {
      if (!alive) return;
      const list = ok(r) && Array.isArray(r.json) ? r.json : [];
      setPublicTiers(list);
      const g = init.group;
      if (g) {
        const first = g.monthly || g.annual;
        setPlan({
          baseTierId: g.basedOn ? g.basedOn.id : null,
          name: init.mode === "duplicate" ? `${g.family} (copy)` : g.family,
          monthly: { on: !!g.monthly, price: g.monthly ? Number(g.monthly.price) : null },
          annual: { on: !!g.annual, price: g.annual ? Number(g.annual.price) : null },
          ftcDaily: first.limit_ftc_daily,
          ftcMonthly: first.limit_ftc_monthly,
          descriptions: (first.descriptions || ["", "", ""]).map((d) => d || ""),
        });
      } else {
        setPlan({
          baseTierId: null, name: "", monthly: { on: true, price: null }, annual: { on: true, price: null },
          ftcDaily: null, ftcMonthly: null, descriptions: ["", "", ""],
        });
      }
    });
    return () => { alive = false; };
  }, [init]);

  const families = useMemo(() => groupFamilies(publicTiers || []), [publicTiers]);
  if (!plan || publicTiers === null) return <Spin style={{ display: "block", margin: "24px auto" }} />;

  const baseFamily = families.find((f) => [f.annual, f.monthly].some((t) => t && t.id === plan.baseTierId));
  const set = (patch) => setPlan({ ...plan, ...patch });

  // Starting from a plan fills in its prices, caps and feature lines.
  const chooseBase = (familyName) => {
    const f = families.find((x) => x.family === familyName);
    if (!f) return;
    const base = f.monthly || f.annual;
    set({
      baseTierId: base.id,
      name: plan.name || `${f.family} Special`,
      monthly: { on: !!f.monthly, price: f.monthly ? Number(f.monthly.price) : null },
      annual: { on: !!f.annual, price: f.annual ? Number(f.annual.price) : null },
      ftcDaily: base.limit_ftc_daily,
      ftcMonthly: base.limit_ftc_monthly,
      descriptions: (base.descriptions || ["", "", ""]).map((d) => d || ""),
    });
  };

  const error = editing
    ? (!(plan.name || "").trim() ? "Give the plan a name." : null)
    : customPlanError(plan);

  const save = async () => {
    setTried(true);
    if (error) return;
    setSaving(true);
    try {
      if (editing) {
        const body = customPlanPayload(plan);
        const monthlyRate = plan.monthly.on ? plan.monthly.price : plan.annual.price;
        for (const cad of ["monthly", "annual"]) {
          const tier = init.group[cad];
          if (!tier) continue;
          const r = await api.updateCustomPlan(tier.id, {
            name: body.name,
            price: String(plan[cad].price),
            descriptions: body.descriptions,
            limit_ftc_daily: body.limit_ftc_daily,
            limit_ftc_monthly: body.limit_ftc_monthly,
            full_monthly_rate: String(monthlyRate),
          });
          if (!ok(r)) throw new Error((r.json && r.json.detail) || "Couldn't save the plan.");
        }
        message.success("Special plan saved.");
        onSaved(init.group.tiers.map((t) => t.id));
      } else {
        const r = await api.createCustomPlan(customPlanPayload(plan));
        if (!ok(r)) throw new Error((r.json && r.json.detail) || "Couldn't save the plan.");
        message.success("Special plan saved — it's now a template too.");
        onSaved((r.json || []).map((t) => t.id));
      }
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const title = { create: "New special plan", duplicate: "Duplicate & edit", edit: "Edit special plan" }[init.mode];
  return (
    <div className="ub-plan-form" style={{ marginTop: 14 }}>
      <span className="ub-step-label" style={{ fontSize: 13 }}>{title}</span>
      <div className="ub-form-grid">
        <div>
          <span className="crm-field-label">Start from</span>
          <Select
            style={{ width: "100%" }}
            value={baseFamily ? baseFamily.family : undefined}
            placeholder="Choose a plan"
            disabled={editing}
            onChange={chooseBase}
            dropdownStyle={{ zIndex: 10060 }}
            options={families.map((f) => ({ value: f.family, label: f.family }))}
          />
        </div>
        <div>
          <span className="crm-field-label">Name</span>
          <Input
            value={plan.name}
            maxLength={80}
            onChange={(e) => set({ name: e.target.value })}
            placeholder={scopeName ? `e.g. Pro for ${scopeName}` : "e.g. Pro Partner"}
          />
        </div>
        <div className="ub-span-2">
          <span className="crm-field-label">Billing terms and price</span>
          {[["monthly", "Monthly"], ["annual", "12-month"]].map(([cad, text]) => (
            <div className="ub-cadence" key={cad}>
              <Checkbox
                checked={plan[cad].on}
                disabled={editing}
                onChange={(e) => set({ [cad]: { ...plan[cad], on: e.target.checked } })}
              >
                <span className="ub-cadence-name">{text}</span>
              </Checkbox>
              <InputNumber
                min={1}
                precision={2}
                prefix="$"
                addonAfter="/ month"
                disabled={!plan[cad].on}
                value={plan[cad].price}
                onChange={(v) => set({ [cad]: { ...plan[cad], price: v } })}
                style={{ width: 190 }}
              />
              <span className="ub-cadence-note">
                {plan[cad].on && Number(plan[cad].price) > 0
                  ? cad === "annual"
                    ? `Billed $${money(Number(plan[cad].price) * 12)} a year`
                    : "Billed every month"
                  : ""}
              </span>
            </div>
          ))}
        </div>
        <div>
          <span className="crm-field-label">FTC requests per day</span>
          <InputNumber
            min={0} precision={0} style={{ width: "100%" }} placeholder="Unlimited"
            value={plan.ftcDaily} onChange={(v) => set({ ftcDaily: v })}
          />
        </div>
        <div>
          <span className="crm-field-label">FTC requests per month</span>
          <InputNumber
            min={0} precision={0} style={{ width: "100%" }} placeholder="Unlimited"
            value={plan.ftcMonthly} onChange={(v) => set({ ftcMonthly: v })}
          />
        </div>
        <div className="ub-span-2">
          <span className="crm-field-label">What&rsquo;s included (shown on the plan card)</span>
          {plan.descriptions.map((d, i) => (
            <Input
              key={i}
              value={d}
              maxLength={200}
              style={{ marginBottom: 6 }}
              placeholder={`Feature line ${i + 1}`}
              onChange={(e) => {
                const next = [...plan.descriptions];
                next[i] = e.target.value;
                set({ descriptions: next });
              }}
            />
          ))}
        </div>
      </div>
      <div className="leadset-hint">
        Every other limit and overage price comes from {baseFamily ? baseFamily.family : "the plan it starts from"}.
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
        {tried && error ? <span className="ub-editor-error">{error}</span> : null}
        <Button onClick={onCancel}>Back</Button>
        <Button type="primary" loading={saving} onClick={save}>Save plan</Button>
      </div>
    </div>
  );
}
